import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MaskLine, FadeUp } from "./Reveal";
import { waLink } from "../config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = ["Dealer", "Wholesaler", "Retailer", "Distributor", "Other"];

const DEALER_WA = waLink(
  "Hello TMV Coffee, I am interested in becoming a dealer. Please share details."
);

const DealerSection = () => {
  const [form, setForm] = useState({
    name: "",
    business_name: "",
    phone: "",
    city: "",
    business_type: "",
    requirement: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, enquiry_type: "dealer" });
      toast.success("Enquiry received. The TMV Coffee team will reach out shortly.");
      setForm({ name: "", business_name: "", phone: "", city: "", business_type: "", requirement: "", message: "" });
    } catch {
      toast.error("Could not send your enquiry. Please try WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="dealer" data-testid="dealer-section" className="bg-bark py-24 md:py-32">
      <div className="mx-auto grid max-w-7xl gap-16 px-5 md:px-10 lg:grid-cols-2">
        <div className="min-w-0">
          <FadeUp>
            <p className="mb-5 text-[11px] uppercase tracking-widest2 text-gold">Grow With Us</p>
          </FadeUp>
          <h2 className="font-tamil text-3xl leading-[1.35] text-cream sm:text-4xl lg:text-[2.6rem]">
            <MaskLine delay={0.05}>உங்கள் Business-க்கு</MaskLine>
            <MaskLine delay={0.18} className="text-gold">
              TMV Coffee-யை இணைத்துக்கொள்ளுங்கள்.
            </MaskLine>
          </h2>
          <FadeUp delay={0.2} className="mt-7 max-w-md text-base leading-relaxed text-cream/70">
            தரமான Coffee மற்றும் Tea products-ஐ உங்கள் customers-க்கு கொண்டு செல்ல TMV
            Coffee-யுடன் இணைந்திடுங்கள்.
          </FadeUp>
          <FadeUp delay={0.3} className="mt-8 flex flex-wrap gap-3">
            {["Dealers", "Wholesalers", "Retailers", "Distributors"].map((t) => (
              <span
                key={t}
                className="rounded-full border border-cream/20 px-4 py-2 text-xs uppercase tracking-[0.15em] text-cream/70"
              >
                {t}
              </span>
            ))}
          </FadeUp>
          <FadeUp delay={0.4} className="mt-10">
            <a
              data-testid="dealer-whatsapp-button"
              href={DEALER_WA}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 rounded-full border border-gold/60 px-7 py-3.5 text-xs font-medium uppercase tracking-[0.15em] text-gold transition-all duration-300 hover:bg-gold hover:text-espresso"
            >
              <WhatsAppIcon className="h-4 w-4" />
              Dealer Enquiry on WhatsApp
            </a>
          </FadeUp>
        </div>

        <FadeUp delay={0.15} className="min-w-0">
          <form
            data-testid="dealer-enquiry-form"
            onSubmit={submit}
            className="rounded-sm border border-cream/10 bg-espresso/40 p-7 backdrop-blur-sm md:p-9"
          >
            <p className="mb-8 font-display text-2xl text-cream">Dealer Enquiry</p>
            <div className="grid gap-6 sm:grid-cols-2">
              <input data-testid="dealer-name-input" className="field-line" placeholder="Name *" value={form.name} onChange={set("name")} />
              <input data-testid="dealer-business-input" className="field-line" placeholder="Business Name" value={form.business_name} onChange={set("business_name")} />
              <input data-testid="dealer-phone-input" className="field-line" placeholder="Phone *" type="tel" value={form.phone} onChange={set("phone")} />
              <input data-testid="dealer-city-input" className="field-line" placeholder="City" value={form.city} onChange={set("city")} />
              <select data-testid="dealer-type-select" className="field-line sm:col-span-2" value={form.business_type} onChange={set("business_type")}>
                <option value="">Business Type</option>
                {TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <input data-testid="dealer-requirement-input" className="field-line sm:col-span-2" placeholder="Requirement (e.g. filter coffee, monthly)" value={form.requirement} onChange={set("requirement")} />
              <textarea data-testid="dealer-message-input" className="field-line sm:col-span-2" rows="2" placeholder="Message" value={form.message} onChange={set("message")} />
            </div>
            <button
              data-testid="dealer-submit-button"
              type="submit"
              disabled={sending}
              className="mt-9 w-full rounded-full bg-gold py-4 text-xs font-semibold uppercase tracking-[0.2em] text-espresso transition-all duration-300 hover:bg-cream disabled:opacity-50"
            >
              {sending ? "Submitting..." : "Submit Enquiry"}
            </button>
          </form>
        </FadeUp>
      </div>
    </section>
  );
};

export const WhatsAppIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.2-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.668-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

export default DealerSection;
